import React from 'react';

/**
 * Candidate Status Utility and Component
 * 
 * Provides consistent status display and styling for candidate statuses throughout the application.
 * Can be used as a React component or imported functions can be used for custom implementations.
 */

/**
 * Status configuration mapping
 * Maps status values to readable names and color classes
 */
const STATUS_CONFIG = {
  'cas_new': {
    label: 'New',
    bgColor: '[background-color:var(--color-brand-gray)]',
    textColor: 'text-white',
    dotColor: 'bg-white'
  },
  'cas_generating_jp_fit': {
    label: 'Generating Job-Profile Fit',
    bgColor: '[background-color:var(--color-brand-yellow)]',
    textColor: 'text-white',
    dotColor: 'bg-white'
  },
  'cas_jp_fit_completed': {
    label: 'Job-Profile Fit Completed',
    bgColor: '[background-color:var(--color-brand-blue)]',
    textColor: 'text-white',
    dotColor: 'bg-white'
  },
  'cas_generating_report': {
    label: 'Generating Report',
    bgColor: '[background-color:var(--color-brand-yellow)]',
    textColor: 'text-white',
    dotColor: 'bg-white'
  },
  'cas_complete': {
    label: 'Ready',
    bgColor: '[background-color:var(--color-brand-green)]',
    textColor: 'text-white',
    dotColor: 'bg-white'
  }
};

/**
 * Gets the status configuration for a given status
 * @param {string} status - The status value
 * @returns {Object} - Status configuration with label and colors
 */
export const getStatusConfig = (status) => {
  if (!status) {
    return {
      label: 'Unknown',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      dotColor: 'bg-gray-500'
    };
  }
  
  // Normalize status to lowercase for lookup
  const normalizedStatus = status.toString().toLowerCase().trim();
  
  // Return the configuration or default to gray if not found
  return STATUS_CONFIG[normalizedStatus] || {
    label: status, // Use original status as label if not in config
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    dotColor: 'bg-gray-500'
  };
};

/**
 * Gets a readable label for a status
 * @param {string} status - The status value
 * @returns {string} - Readable status label
 */
export const getStatusLabel = (status) => {
  return getStatusConfig(status).label;
};

/**
 * Gets the color classes for a status
 * @param {string} status - The status value
 * @returns {Object} - Object containing bgColor, textColor, and dotColor classes
 */
export const getStatusColors = (status) => {
  const config = getStatusConfig(status);
  return {
    bgColor: config.bgColor,
    textColor: config.textColor,
    dotColor: config.dotColor
  };
};

/**
 * CandidateStatus Component
 * 
 * Renders a styled status badge for candidate statuses
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - The status value to display
 * @param {boolean} props.showDot - Whether to show a colored dot indicator (default: false)
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} props.className - Additional CSS classes to apply
 * @returns {JSX.Element} - Rendered status badge
 */
const CandidateStatus = ({ 
  status, 
  showDot = false, 
  size = 'md',
  className = '' 
}) => {
  const config = getStatusConfig(status);
  
  // Size-based classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm'
  };
  
  const dotSizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5'
  };
  
  return (
    <span 
      className={`
        inline-flex items-center gap-1.5
        ${sizeClasses[size]}
        ${config.bgColor} 
        ${config.textColor}
        leading-5 font-semibold rounded-full
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {showDot && (
        <span 
          className={`
            ${dotSizeClasses[size]} 
            ${config.dotColor} 
            rounded-full
          `.trim().replace(/\s+/g, ' ')}
        />
      )}
      {config.label}
    </span>
  );
};

export default CandidateStatus;
